import { useMemo, useState } from 'react';
import type { RumHeatCell } from '@/api/types';

/**
 * Sequential blue ramp — shared with `Heatmap.tsx` on purpose.
 *
 * Two heatmaps on one dashboard sharing a ramp means a reader learns "darker = more" once. Giving
 * this one its own hue would imply the two encode different things, which they do not.
 *
 * VALIDATED, not chosen by eye. From the dataviz skill directory:
 *   node scripts/validate_palette.js "#8ab8f0,#5f9ae8,#3a7cd8,#2262b8,#164a92,#0e346c,#071f45" \
 *     --mode light --surface "#ffffff" --ordinal      → all four checks PASS
 *
 * The previous ramp FAILED twice and both failures were visible in the rendered chart: its light end
 * sat at 1.32:1 against white, so the lowest band was all but invisible, and two mid steps differed
 * by ΔL 0.047 — indistinguishable, which silently merged two magnitude bands into one.
 */
// No zero step here: a cell with no clicks is simply absent from `cells` and never drawn, so every
// painted cell is a real measurement. (`Heatmap.tsx` has a dense grid and does need one.)
const RAMP = [
  '#8ab8f0', // 2.06:1 vs white — the lightest step that is still visibly a step
  '#5f9ae8',
  '#3a7cd8',
  '#2262b8',
  '#164a92',
  '#0e346c',
  '#071f45',
] as const;

/** Rage overlay. Status red, reserved — never a categorical slot. */
const RAGE = '#dc2626';

interface ClickHeatmapProps {
  cells: RumHeatCell[];
  /** Grid resolution from the payload — never hardcoded, so the server can change it. */
  grid: number;
  /** Viewport aspect ratio to render at. 16:10 is the desktop median. */
  aspect?: number;
}

/**
 * Where people click, in normalised viewport space.
 *
 * <b>Coordinates are FRACTIONS of the viewport, not pixels</b> — which is what makes a phone and a
 * 27-inch monitor comparable at all. A pixel heatmap would put every mobile click in the top-left
 * corner of a desktop-shaped canvas.
 *
 * <b>Rage clicks are an overlay, not a hotter colour.</b> Folding them into the same ramp would say
 * "more clicks here", when the actual finding is "people are hitting this and nothing happens".
 * Those are different problems and they need different marks.
 *
 * The grid is coarse (40×40 by default) on purpose: fine enough to locate a button, coarse enough
 * that one visitor's clicks never form a recognisable path.
 */
export function ClickHeatmap({ cells, grid, aspect = 0.625 }: ClickHeatmapProps) {
  const [hover, setHover] = useState<RumHeatCell | null>(null);
  const [showRage, setShowRage] = useState(true);

  const max = useMemo(
    () => cells.reduce((peak, cell) => Math.max(peak, cell.count), 0),
    [cells],
  );

  if (cells.length === 0) {
    return (
      <div className="empty-state t-body">
        No clicks in the live window yet. Clicks appear here within seconds of happening.
      </div>
    );
  }

  const step = 100 / grid;

  return (
    <div className="clickheat">
      <div className="clickheat-controls">
        <label className="t-label clickheat-toggle">
          <input
            type="checkbox"
            checked={showRage}
            onChange={(e) => setShowRage(e.target.checked)}
          />
          Highlight rage clicks
        </label>
        <span className="t-label subtle">
          {max > 0 ? `busiest cell: ${max} click${max === 1 ? '' : 's'}` : 'no clicks yet'}
        </span>
      </div>

      <div className="clickheat-frame" style={{ paddingBottom: `${aspect * 100}%` }}>
        <svg
          className="clickheat-svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-label="Click positions across the viewport"
        >
          {/* Thirds, as a spatial reference. Without them a cell in open space is unplaceable —
              "somewhere in the middle" is not an answer anyone can act on. */}
          {[1, 2].map((n) => (
            <g key={n} className="clickheat-guide">
              <line x1={(n * 100) / 3} y1="0" x2={(n * 100) / 3} y2="100" />
              <line x1="0" y1={(n * 100) / 3} x2="100" y2={(n * 100) / 3} />
            </g>
          ))}

          {cells.map((cell) => {
            // A present cell always gets at least the first ramp step. Only a truly absent cell
            // gets ZERO, and absent cells are not in `cells` at all — so "faint" always means
            // "someone clicked here", never "no data".
            const shade = max === 0 ? 0 : Math.min(
              RAMP.length - 1,
              Math.max(0, Math.ceil((cell.count / max) * RAMP.length) - 1),
            );

            return (
              <rect
                key={`${cell.col},${cell.row}`}
                x={cell.col * step}
                y={cell.row * step}
                width={step}
                height={step}
                fill={RAMP[shade]}
                onMouseEnter={() => setHover(cell)}
                onMouseLeave={() => setHover(null)}
              />
            );
          })}

          {showRage &&
            cells
              .filter((cell) => cell.rage_count > 0)
              .map((cell) => (
                <rect
                  key={`rage-${cell.col},${cell.row}`}
                  x={cell.col * step}
                  y={cell.row * step}
                  width={step}
                  height={step}
                  fill="none"
                  stroke={RAGE}
                  strokeWidth={0.6}
                  // Identity is not colour alone: the ring is a second, non-chromatic cue, so the
                  // overlay survives a red-green deficiency and a black-and-white print.
                  strokeDasharray="1.5 1"
                  onMouseEnter={() => setHover(cell)}
                  onMouseLeave={() => setHover(null)}
                />
              ))}
        </svg>

        {hover && (
          <div
            className="clickheat-tip t-label"
            style={{
              left: `${(hover.col + 0.5) * step}%`,
              top: `${(hover.row + 0.5) * step}%`,
            }}
          >
            {hover.count} click{hover.count === 1 ? '' : 's'}
            {hover.rage_count > 0 && <> · <span className="note-error">{hover.rage_count} rage</span></>}
            <div className="subtle">
              {Math.round((hover.col / grid) * 100)}% across, {Math.round((hover.row / grid) * 100)}% down
            </div>
          </div>
        )}
      </div>

      <div className="clickheat-legend t-label">
        <span className="subtle">fewer</span>
        {RAMP.map((colour) => (
          <span key={colour} className="clickheat-swatch" style={{ background: colour }} />
        ))}
        <span className="subtle">more clicks</span>
        {showRage && (
          <span className="clickheat-legend-rage">
            <span className="clickheat-swatch clickheat-swatch-rage" /> rage clicks
          </span>
        )}
      </div>
    </div>
  );
}
